
import React from "react";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { FileText, Eye, Activity } from "lucide-react";
import { AnalyticsData } from "@/hooks/useAnalytics";

interface AnalyticsCardsProps {
  analyticsData: AnalyticsData | null;
  analyticsLoading: boolean;
  analyticsError: Error | null;
  posts: any[];
}

const AnalyticsCards = ({ 
  analyticsData, 
  analyticsLoading, 
  analyticsError,
  posts
}: AnalyticsCardsProps) => {
  // Safe values with fallbacks
  const totalPosts = analyticsData?.totalPosts || 0;
  const publishedPosts = analyticsData?.publishedPosts || 0;
  const draftPosts = analyticsData?.draftPosts || 0;
  const totalViews = analyticsData?.totalViews || 0;
  const totalClicks = analyticsData?.totalClicks || 0;
  const ctr = analyticsData?.ctr || 0;
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
          <CardTitle className="text-sm font-medium">Total Posts</CardTitle>
          <FileText className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          {analyticsLoading ? (
            <div className="text-2xl font-bold">Loading...</div>
          ) : analyticsError ? (
            <div className="text-2xl font-bold">{posts.length}</div>
          ) : (
            <div className="text-2xl font-bold">{totalPosts}</div>
          )}
          <p className="text-xs text-muted-foreground">
            {analyticsLoading ? (
              "Loading post counts..."
            ) : analyticsError ? (
              `${posts.filter(p => p.published).length} published, ${posts.filter(p => !p.published).length} drafts`
            ) : (
              `${publishedPosts} published, ${draftPosts} drafts`
            )}
          </p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
          <CardTitle className="text-sm font-medium">Total Views</CardTitle>
          <Eye className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          {analyticsLoading ? (
            <div className="text-2xl font-bold">Loading...</div>
          ) : analyticsError ? (
            <div className="text-2xl font-bold">N/A</div>
          ) : (
            <div className="text-2xl font-bold">{totalViews.toLocaleString()}</div>
          )}
          <p className="text-xs text-muted-foreground">
            {analyticsLoading ? (
              "Loading view data..."
            ) : analyticsError ? (
              "Error loading view data"
            ) : (
              `Avg ${publishedPosts > 0 
                ? Math.round(totalViews / publishedPosts) 
                : 0} views per post`
            )}
          </p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
          <CardTitle className="text-sm font-medium">CTR to Magic Letter Box</CardTitle>
          <Activity className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          {analyticsLoading ? (
            <div className="text-2xl font-bold">Loading...</div>
          ) : analyticsError ? (
            <div className="text-2xl font-bold">N/A</div>
          ) : (
            <div className="text-2xl font-bold">{ctr.toFixed(1)}%</div>
          )}
          <p className="text-xs text-muted-foreground">
            {analyticsLoading ? (
              "Loading CTR data..."
            ) : analyticsError ? (
              "Error loading CTR data"
            ) : (
              `${totalClicks.toLocaleString()} clicks / ${totalViews.toLocaleString()} views`
            )}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsCards;
